import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/shared/ui'
import { useLegalEntityDetails } from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { LegalEntityStatusBadge } from './LegalEntityStatusBadge'
import { FinanceBlock } from './FinanceBlock'

interface Props {
  entityId: number | null
  onClose: () => void
  onApprove: (id: number, name: string) => void
  onReject: (id: number, name: string) => void
  onDisable: (id: number, name: string) => void
  onEnable: (id: number) => void
  actionLoading?: boolean
}

const Field: React.FC<{ label: string; value?: string | null | undefined }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 break-words">{value || '—'}</div>
  </div>
)

export const LegalEntityDetailModal: React.FC<Props> = ({
  entityId,
  onClose,
  onApprove,
  onReject,
  onDisable,
  onEnable,
  actionLoading = false,
}) => {
  const { hasPermission } = useAuth()
  const { data, isLoading, isError } = useLegalEntityDetails(entityId || 0, {
    enabled: !!entityId,
  })

  if (!entityId) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {data?.name || 'Юридическое лицо'}
            </h2>
            {data && <LegalEntityStatusBadge status={data.status} />}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {isError ? (
          <div className="p-6 text-sm text-gray-500">Не удалось загрузить карточку</div>
        ) : isLoading || !data ? (
          <div className="p-6 text-sm text-gray-500">Загрузка...</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Полное наименование" value={data.fullName} />
              <Field label="Название" value={data.name} />
              <Field label="ИНН" value={data.inn} />
              <Field label="КПП" value={data.kpp} />
              <Field label="ОГРН" value={data.ogrn} />
              <Field label="Юридический адрес" value={data.legalAddress} />
              <Field label="Банк" value={data.bankName} />
              <Field label="Расчётный счёт" value={data.bankAccount} />
              <Field label="БИК" value={data.bankBik} />
              <Field label="Контактное лицо" value={data.contactPerson} />
              <Field label="Телефон" value={data.phone} />
              <Field label="Email" value={data.email} />
              <Field label="Дата регистрации" value={data.createdAtFormatted} />
              <Field label="Дата обновления" value={data.updatedAtFormatted} />
              <Field label="Одобрено" value={data.approvedAtFormatted} />
              <Field label="Отключено" value={data.disabledAtFormatted} />
            </div>

            {data.rejectionReason && (
              <div className="rounded-lg bg-error-50 p-3">
                <div className="text-xs text-error-700 mb-1">Причина отклонения</div>
                <div className="text-sm text-gray-900">{data.rejectionReason}</div>
              </div>
            )}
            {data.disableReason && (
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs text-gray-500 mb-1">Причина отключения</div>
                <div className="text-sm text-gray-900">{data.disableReason}</div>
              </div>
            )}

            <FinanceBlock entityId={data.legalEntityId} entityName={data.name} />
          </div>
        )}

        {data && (
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            {data.status === 0 && (
              <>
                {hasPermission('legal_entities', 'approve') && (
                  <Button
                    onClick={() => onApprove(data.legalEntityId, data.name)}
                    isLoading={actionLoading}
                  >
                    Одобрить
                  </Button>
                )}
                {hasPermission('legal_entities', 'reject') && (
                  <Button
                    variant="danger"
                    onClick={() => onReject(data.legalEntityId, data.name)}
                  >
                    Отклонить
                  </Button>
                )}
              </>
            )}
            {data.status === 1 && hasPermission('legal_entities', 'disable') && (
              <Button
                variant="danger"
                onClick={() => onDisable(data.legalEntityId, data.name)}
              >
                Отключить
              </Button>
            )}
            {data.status === 3 && hasPermission('legal_entities', 'enable') && (
              <Button
                onClick={() => onEnable(data.legalEntityId)}
                isLoading={actionLoading}
              >
                Включить
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Закрыть
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
